/**
 * Tier 2 dynamic-block OBJECTS writers — DYN-2.1 + 2.2.
 *
 * Symmetric to `dxfBridge.dynamic.parser.tier2.ts`. Every write* function
 * emits a single `DxfDynamicBlockObject` for a Tier 2 parameter / action
 * / table; the top-level `emitDynamicBlockObjects` in
 * `dxfBridge.dynamic.writer.ts` calls these through the dispatchers at
 * the bottom of this module.
 *
 * Group-code conventions mirror the parser — keep the two files in sync.
 */
import type { DxfDynamicBlockObject, DxfGroup } from "@dxf-ai-editor/dxf-io";

import type {
  AngularConstraintParameter,
  ArrayAction,
  BlockPropertiesTable,
  DynamicAction,
  DynamicParameter,
  HorizontalConstraintParameter,
  LookupAction,
  LookupParameter,
  LookupTable,
  PointParameter,
  PolarParameter,
  RotateAction,
  RotationParameter,
  ScaleAction,
  ValueSet,
  VerticalConstraintParameter,
  XYParameter,
} from "./dynamicBlock";
import { pushGroup } from "./dxfBridge.dynamic.shared";

// ═══════════════════════════════════════════════════════════════════════════
// Writer context (shared shape with tier1)
// ═══════════════════════════════════════════════════════════════════════════

export type Tier2WriterContext = {
  nextHandle: () => string;
  blockRecordHandle: string;
  /** Map from LookupTable.id → emitted DXF handle so Lookup parameters
   *  can point at their table with group 340. */
  tableIdToHandle: Map<string, string>;
};

// ═══════════════════════════════════════════════════════════════════════════
// Shared helpers
// ═══════════════════════════════════════════════════════════════════════════

type AxisValueSetCodes = {
  modeCode: number;
  incrementCode: number;
  minCode: number;
  maxCode: number;
  listCode: number;
};

function emitAxisValueSet(
  groups: DxfGroup[],
  valueSet: ValueSet,
  codes: AxisValueSetCodes,
): void {
  const mode =
    valueSet.mode === "increment" ? 1 : valueSet.mode === "list" ? 2 : 0;
  pushGroup(groups, codes.modeCode, mode);
  pushGroup(groups, codes.incrementCode, valueSet.increment);
  if (valueSet.min !== undefined) pushGroup(groups, codes.minCode, valueSet.min);
  if (valueSet.max !== undefined) pushGroup(groups, codes.maxCode, valueSet.max);
  for (const v of valueSet.listValues) {
    pushGroup(groups, codes.listCode, v);
  }
}

function emitBaseParamHeader(
  groups: DxfGroup[],
  className: string,
  handle: string,
  ownerHandle: string,
  subclasses: string[],
  name: string,
  label: string,
  description: string,
): void {
  pushGroup(groups, 0, className);
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ownerHandle);
  for (const sub of subclasses) pushGroup(groups, 100, sub);
  pushGroup(groups, 1, name);
  if (label) pushGroup(groups, 3, label);
  if (description) pushGroup(groups, 4, description);
}

function emitBaseFlags(
  groups: DxfGroup[],
  chainActions: boolean,
  showGrip: boolean,
): void {
  pushGroup(groups, 280, chainActions ? 1 : 0);
  pushGroup(groups, 281, showGrip ? 1 : 0);
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 2 parameters
// ═══════════════════════════════════════════════════════════════════════════

function writePointParameter(
  p: PointParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKPOINTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock1PtParameter",
      "AcDbBlockPointParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.basePoint.x);
  pushGroup(groups, 20, p.basePoint.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.currentPoint.x);
  pushGroup(groups, 21, p.currentPoint.y);
  pushGroup(groups, 31, 0);
  emitAxisValueSet(groups, p.xValueSet, {
    modeCode: 170,
    incrementCode: 140,
    minCode: 141,
    maxCode: 142,
    listCode: 145,
  });
  emitAxisValueSet(groups, p.yValueSet, {
    modeCode: 171,
    incrementCode: 143,
    minCode: 144,
    maxCode: 146,
    listCode: 147,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKPOINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writePolarParameter(
  p: PolarParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKPOLARPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockPolarParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.basePoint.x);
  pushGroup(groups, 20, p.basePoint.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.secondPoint.x);
  pushGroup(groups, 21, p.secondPoint.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 140, p.defaultDistance);
  pushGroup(groups, 141, p.currentDistance);
  pushGroup(groups, 50, p.defaultAngle);
  pushGroup(groups, 51, p.currentAngle);
  emitAxisValueSet(groups, p.distanceValueSet, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  emitAxisValueSet(groups, p.angleValueSet, {
    modeCode: 171,
    incrementCode: 146,
    minCode: 147,
    maxCode: 148,
    listCode: 149,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKPOLARPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeXYParameter(
  p: XYParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKXYPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockXYParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.basePoint.x);
  pushGroup(groups, 20, p.basePoint.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.secondPoint.x);
  pushGroup(groups, 21, p.secondPoint.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 140, p.defaultX);
  pushGroup(groups, 141, p.defaultY);
  pushGroup(groups, 142, p.currentX);
  pushGroup(groups, 143, p.currentY);
  emitAxisValueSet(groups, p.xValueSet, {
    modeCode: 170,
    incrementCode: 144,
    minCode: 145,
    maxCode: 146,
    listCode: 147,
  });
  emitAxisValueSet(groups, p.yValueSet, {
    modeCode: 171,
    incrementCode: 148,
    minCode: 149,
    maxCode: 1070,
    listCode: 1071,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKXYPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeRotationParameter(
  p: RotationParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKROTATIONPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock1PtParameter",
      "AcDbBlockRotationParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.basePoint.x);
  pushGroup(groups, 20, p.basePoint.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 40, p.radius);
  pushGroup(groups, 50, p.defaultAngle);
  pushGroup(groups, 51, p.currentAngle);
  emitAxisValueSet(groups, p.angleValueSet, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKROTATIONPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeLookupParameter(
  p: LookupParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKLOOKUPPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock1PtParameter",
      "AcDbBlockLookupParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.position.x);
  pushGroup(groups, 20, p.position.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 290, p.reverseLookup ? 1 : 0);
  // Reference to the lookup table. If we already emitted a table with
  // this id, use that handle; otherwise fall back to the id itself so
  // round-trip preserves intent even when the table is authored later.
  const tableHandle = ctx.tableIdToHandle.get(p.lookupTableId) ?? p.lookupTableId;
  pushGroup(groups, 340, tableHandle);
  if (p.currentLookupRowId) pushGroup(groups, 341, p.currentLookupRowId);
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKLOOKUPPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 2 constraint parameters (DYN-2 Batch C) — writers symmetric to
// the Batch C parsers in dxfBridge.dynamic.parser.tier2.ts.
// ═══════════════════════════════════════════════════════════════════════════

function writeHorizontalConstraintParameter(
  p: HorizontalConstraintParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockHorizontalConstraintParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.pointA.x);
  pushGroup(groups, 20, p.pointA.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.pointB.x);
  pushGroup(groups, 21, p.pointB.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 140, p.defaultDistance);
  pushGroup(groups, 141, p.currentDistance);
  emitAxisValueSet(groups, p.distanceValueSet, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKHORIZONTALCONSTRAINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeVerticalConstraintParameter(
  p: VerticalConstraintParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKVERTICALCONSTRAINTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock2PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockVerticalConstraintParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.pointA.x);
  pushGroup(groups, 20, p.pointA.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.pointB.x);
  pushGroup(groups, 21, p.pointB.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 140, p.defaultDistance);
  pushGroup(groups, 141, p.currentDistance);
  emitAxisValueSet(groups, p.distanceValueSet, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKVERTICALCONSTRAINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

function writeAngularConstraintParameter(
  p: AngularConstraintParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = p.id || ctx.nextHandle();
  emitBaseParamHeader(
    groups,
    "ACDBBLOCKANGULARCONSTRAINTPARAMETER",
    handle,
    ctx.blockRecordHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockParameter",
      "AcDbBlock3PtParameter",
      "AcDbBlockConstraintParameter",
      "AcDbBlockAngularConstraintParameter",
    ],
    p.name,
    p.label,
    p.description,
  );
  pushGroup(groups, 10, p.pointA.x);
  pushGroup(groups, 20, p.pointA.y);
  pushGroup(groups, 30, 0);
  pushGroup(groups, 11, p.pointB.x);
  pushGroup(groups, 21, p.pointB.y);
  pushGroup(groups, 31, 0);
  pushGroup(groups, 12, p.vertex.x);
  pushGroup(groups, 22, p.vertex.y);
  pushGroup(groups, 32, 0);
  pushGroup(groups, 140, p.defaultAngle);
  pushGroup(groups, 141, p.currentAngle);
  emitAxisValueSet(groups, p.angleValueSet, {
    modeCode: 170,
    incrementCode: 142,
    minCode: 143,
    maxCode: 144,
    listCode: 145,
  });
  emitBaseFlags(groups, p.chainActions, p.showGrip);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKANGULARCONSTRAINTPARAMETER",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Tier 2 actions
// ═══════════════════════════════════════════════════════════════════════════

function emitActionHeader(
  groups: DxfGroup[],
  className: string,
  handle: string,
  paramHandle: string,
  subclasses: string[],
  name: string,
): void {
  pushGroup(groups, 0, className);
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, paramHandle);
  for (const sub of subclasses) pushGroup(groups, 100, sub);
  if (name) pushGroup(groups, 1, name);
}

function emitActionSelection(
  groups: DxfGroup[],
  paramHandle: string,
  selectionEntityIds: string[],
): void {
  pushGroup(groups, 340, paramHandle);
  pushGroup(groups, 91, selectionEntityIds.length);
  for (const id of selectionEntityIds) pushGroup(groups, 340, id);
}

function writeRotateAction(
  a: RotateAction,
  paramHandle: string,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = a.id || ctx.nextHandle();
  emitActionHeader(
    groups,
    "ACDBBLOCKROTATIONACTION",
    handle,
    paramHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockAction",
      "AcDbBlockActionWithBasePt",
      "AcDbBlockRotationAction",
    ],
    a.name,
  );
  emitActionSelection(groups, paramHandle, a.selectionEntityIds);
  pushGroup(groups, 40, a.angleMultiplier);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKROTATIONACTION",
    handle,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

function writeScaleAction(
  a: ScaleAction,
  paramHandle: string,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = a.id || ctx.nextHandle();
  emitActionHeader(
    groups,
    "ACDBBLOCKSCALEACTION",
    handle,
    paramHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockAction",
      "AcDbBlockScaleAction",
    ],
    a.name,
  );
  emitActionSelection(groups, paramHandle, a.selectionEntityIds);
  const typeRaw =
    a.scaleType === "x-only" ? 1 : a.scaleType === "y-only" ? 2 : 0;
  pushGroup(groups, 170, typeRaw);
  pushGroup(groups, 40, a.scaleMultiplier);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKSCALEACTION",
    handle,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

function writeArrayAction(
  a: ArrayAction,
  paramHandle: string,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = a.id || ctx.nextHandle();
  emitActionHeader(
    groups,
    "ACDBBLOCKARRAYACTION",
    handle,
    paramHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockAction",
      "AcDbBlockArrayAction",
    ],
    a.name,
  );
  emitActionSelection(groups, paramHandle, a.selectionEntityIds);
  pushGroup(groups, 42, a.columnSpacing);
  pushGroup(groups, 43, a.rowSpacing);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKARRAYACTION",
    handle,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

function writeLookupAction(
  a: LookupAction,
  paramHandle: string,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = a.id || ctx.nextHandle();
  emitActionHeader(
    groups,
    "ACDBBLOCKLOOKUPACTION",
    handle,
    paramHandle,
    [
      "AcDbEvalExpr",
      "AcDbBlockElement",
      "AcDbBlockAction",
      "AcDbBlockLookupAction",
    ],
    a.name,
  );
  // Lookup action references ONLY the driving parameter — no selection
  // set (cascades to OTHER parameters through the lookup table).
  pushGroup(groups, 340, paramHandle);
  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKLOOKUPACTION",
    handle,
    ownerHandle: paramHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Lookup table / block properties table
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Emit an `ACDBBLOCKPROPERTIESTABLE` covering a lookup table. Mirrors the
 * reader in `parseBlockPropertiesTable`.
 */
export function writeLookupTable(
  table: LookupTable,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = table.id || ctx.nextHandle();
  ctx.tableIdToHandle.set(table.id, handle);

  pushGroup(groups, 0, "ACDBBLOCKPROPERTIESTABLE");
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbBlockPropertiesTable");
  pushGroup(groups, 1, table.name);
  pushGroup(groups, 91, table.inputColumns.length);
  pushGroup(groups, 92, table.rows.length);

  for (const col of table.inputColumns) {
    // Column label — use parameterId as a stable label (UI can remap).
    pushGroup(groups, 2, col.parameterId);
    pushGroup(groups, 340, col.parameterId);
  }

  // Rows — cells then optional output value
  for (const row of table.rows) {
    for (const col of table.inputColumns) {
      const cell = row.inputValues[col.id];
      const serialized =
        cell === undefined
          ? ""
          : typeof cell === "boolean"
            ? cell
              ? "1"
              : "0"
            : String(cell);
      pushGroup(groups, 301, serialized);
    }
    if (row.outputValue !== undefined) {
      const out =
        typeof row.outputValue === "boolean"
          ? row.outputValue
            ? "1"
            : "0"
          : String(row.outputValue);
      pushGroup(groups, 302, out);
    }
  }

  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKPROPERTIESTABLE",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

/**
 * Emit the user-defined block properties table (BTABLE). Uses the same
 * `ACDBBLOCKPROPERTIESTABLE` class as the lookup table but distinguishes
 * itself by populating group 70 with per-column type codes.
 */
export function writeBlockPropertiesTable(
  table: BlockPropertiesTable,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject {
  const groups: DxfGroup[] = [];
  const handle = ctx.nextHandle();

  pushGroup(groups, 0, "ACDBBLOCKPROPERTIESTABLE");
  pushGroup(groups, 5, handle);
  pushGroup(groups, 330, ctx.blockRecordHandle);
  pushGroup(groups, 100, "AcDbBlockPropertiesTable");
  pushGroup(groups, 1, "BlockPropertiesTable");
  pushGroup(groups, 91, table.entries.length);
  pushGroup(groups, 92, 0); // rows = 0 for BTABLE — entries carry defaults

  for (const e of table.entries) {
    pushGroup(groups, 2, e.label || e.key);
    const typeRaw =
      e.type === "number" ? 1 : e.type === "boolean" ? 2 : 0;
    pushGroup(groups, 70, typeRaw);
    if (e.defaultValue !== undefined) {
      const out =
        typeof e.defaultValue === "boolean"
          ? e.defaultValue
            ? "1"
            : "0"
          : String(e.defaultValue);
      pushGroup(groups, 302, out);
    }
    if (e.formula) pushGroup(groups, 300, e.formula);
  }

  return {
    kind: "dynamic-block-object",
    type: "ACDBBLOCKPROPERTIESTABLE",
    handle,
    ownerHandle: ctx.blockRecordHandle,
    rawGroups: groups,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Dispatchers — used by the top-level writer
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tier 2 parameter dispatcher. Returns `undefined` if the parameter is
 * not Tier 2 (the caller should handle it via Tier 1 emitters).
 */
export function tryWriteTier2Parameter(
  p: DynamicParameter,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject | undefined {
  switch (p.kind) {
    case "point":
      return writePointParameter(p, ctx);
    case "polar":
      return writePolarParameter(p, ctx);
    case "xy":
      return writeXYParameter(p, ctx);
    case "rotation":
      return writeRotationParameter(p, ctx);
    case "lookup":
      return writeLookupParameter(p, ctx);
    case "constraint-horizontal":
      return writeHorizontalConstraintParameter(p, ctx);
    case "constraint-vertical":
      return writeVerticalConstraintParameter(p, ctx);
    case "constraint-angular":
      return writeAngularConstraintParameter(p, ctx);
    default:
      return undefined;
  }
}

/**
 * Tier 2 action dispatcher. Returns `undefined` if the action is not Tier 2.
 */
export function tryWriteTier2Action(
  a: DynamicAction,
  paramHandle: string,
  ctx: Tier2WriterContext,
): DxfDynamicBlockObject | undefined {
  switch (a.kind) {
    case "rotate":
      return writeRotateAction(a, paramHandle, ctx);
    case "scale":
      return writeScaleAction(a, paramHandle, ctx);
    case "array":
      return writeArrayAction(a, paramHandle, ctx);
    case "lookup":
      return writeLookupAction(a, paramHandle, ctx);
    default:
      return undefined;
  }
}
